// DsIcon — implementa o seletor de elemento `svg` do canvas (DESIGN-SYSTEM.md §3.4):
//   `svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:1.7;
//        stroke-linecap:round;stroke-linejoin:round}` em 129 dos 143 artboards.
//
// É o maior delta visual do sistema: o app tem hoje 392 ocorrências de `-bold*` e
// 234 de `-fill` contra 157 de traço. Aqui o iconset é FECHADO no conjunto `ds:*`
// de traço; não há prop para escolher o ícone cheio.
//
// ----------------------------------------------------------------------
// RODADA DE CONSERTO DA CAMADA COMPARTILHADA — DECISOES-PENDENTES.md §3.1
// Pedido por 14 LOTES (L02, L05, L06, L07, L08, L09, L10, L12, L15, L18, L19,
// L20, L23, L25) — a lacuna mais pedida da rodada inteira.
//
// O mapa tinha 23 nomes utilitários e NENHUM de domínio. Cada lote contornou por
// substituição genérica (`calendario` no lugar de relógio, `exportar` no lugar de
// cifrão, `atualizar` no lugar de reverter) ou por arquivo local com o `d` literal
// do artboard (L06, L15). A tela perdia significado e o mesmo glifo aparecia com
// dois sentidos — L23 mediu isso: `atualizar` no botão "Atualizar" E no item
// "Reverter Exclusão" da mesma tela.
//
// O que mudou aqui:
//   1. `DsIconName` passou de 23 para 56 nomes. Os 33 novos são glifos de DOMÍNIO
//      (documento, guia, nota, cifrão, relógio, trofeu, processo, entrada-saída…),
//      todos com corpo extraído dos artboards `*.dc.html` — a mesma convenção dos
//      23 primeiros. A lista de nomes e corpos foi entregue ao grupo 'infra', que
//      registra em `src/components/iconify/icon-sets.ts` sob o prefixo `ds:`.
//   2. `DsIconSize` ganhou os tamanhos que o canvas realmente instancia e que
//      faltavam: 12, 13, 17, 24, 26, 30 e 32. (Contagem medida nos 143 artboards:
//      18px×1068, 20px×800, 14px×288, 16px×206, 22px×110, 15px×88, 12px×64,
//      26px×54, 17px×36, 32px×18, 44px×16, 30px×16, 24px×12, 13px×4.)
//   3. `strokePorTamanho` passou a devolver 1.6 para 24/26/30/32 — é o valor
//      literal desses artboards (`stroke-width="1.6"` em 24px×1, 26px×26, 30px×8).
//      Nenhum tamanho já existente mudou de espessura.
//
// Divergência medida e NÃO aplicada: 54 das 110 ocorrências de `width:22px` trazem
// `stroke-width="1.6"` explícito e as outras 56 herdam 1.7 do seletor `svg`. Empate;
// mexer aqui mudaria o tile de 40×40 do KPI em dezenas de telas sem ganho de
// fidelidade demonstrável. Fica em 1.7.
//
// Regra de migração não bloqueante (§3.4): o mapa continua incompleto de propósito.
// Enquanto um nome não existir, a tela usa <Iconify> com um ícone de TRAÇO
// (`-linear`/`-outline`/`-line`). Quem precisar de um nome novo acrescenta abaixo,
// registra o corpo em `icon-sets.ts` sob `ds:` e anota no mailbox.
//
// Divergência registrada contra a spec: §3.4 cita o iconset `solar:*-linear`, mas
// nenhum desses nomes existe em `icon-sets.ts` (os `solar:` registrados lá são
// `-bold`/`-fill`), então cada um cairia na CDN do Iconify em runtime — contra a
// decisão F4-T3 anotada em `iconify.tsx`, que é manter os ícones embutidos para o
// app funcionar offline. E a coleção Solar só tem `add-circle`/`close-circle`/
// `check-circle`, com o círculo em volta, enquanto o canvas desenha o glifo nu.
// Por isso o kit registra o conjunto `ds:*` com os corpos literais dos artboards.

import type { IconifyName } from 'src/components/iconify';
import type { Theme, SxProps } from '@mui/material/styles';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

/**
 * Nomes do canvas. União FECHADA — a spec §1.2 proíbe passthrough de nome de
 * iconset do MUI/Iconify por aqui.
 */
export type DsIconName =
  // --- utilitários (os 23 originais) ---
  | 'buscar'
  | 'mais'
  | 'atualizar'
  | 'editar'
  | 'excluir'
  | 'olho'
  | 'fechar'
  | 'chevron-esq'
  | 'chevron-dir'
  | 'chevron-baixo'
  | 'chevron-cima'
  | 'filtro'
  | 'baixar'
  | 'enviar'
  | 'anexo'
  | 'calendario'
  | 'usuario'
  | 'alerta'
  | 'info'
  | 'check'
  | 'mais-opcoes'
  | 'exportar'
  | 'imprimir'
  // --- documento e papel (§3.1: L02, L05, L06, L08, L15, L19, L20, L25) ---
  /** Folha com canto dobrado — nav "Processo Eletrônico", KPI de total de registros. */
  | 'documento'
  /** Folha com canto dobrado E duas linhas — cartão "Consultar Guias", relatórios. */
  | 'guia'
  /** Folha com canto dobrado E um check — KPI de notas/ordens conferidas. */
  | 'documento-check'
  /** Retângulo alto com três linhas — nota fiscal NFS-e. */
  | 'nota'
  /** Cartão com cabeçalho e duas linhas — blocos de débito da conta fiscal. */
  | 'folha'
  /** Prancheta com presilha — espaço de trabalho, ação fiscal. */
  | 'prancheta'
  /** Livro com lombada — "Abra processos e acompanhe protocolos". */
  | 'processo'
  /** Quatro linhas decrescentes — cartão "Conta Fiscal / Extrato consolidado". */
  | 'extrato'
  // --- dinheiro (§3.1: L05, L15, L19, L25) ---
  /** Cifrão nu com marcas — KPI "ISS Estimado a Recuperar". */
  | 'cifrao'
  /** Cifrão dentro de círculo — "Veja seus débitos e gere guias". */
  | 'moeda'
  /** Cartão com tarja — cartão "Gerenciar Crédito Tributário". */
  | 'credito'
  /** Círculo com check — cartão "Gerenciar Pagamentos". */
  | 'pagamento'
  /** Cubo — cartão "Consultar Parcelas". */
  | 'parcela'
  /** Cruz de repartição — cartão "Parcelamento". */
  | 'parcelamento'
  /** Impressora com folha saindo — cartão "Emissão de Guias". */
  | 'emissao'
  // --- tempo e medida (§3.1: L02, L05, L07, L08, L12, L18, L19, L25) ---
  /** Relógio — tempo médio, próxima execução, "Em Dia". */
  | 'relogio'
  /** Barras verticais — nav "Análise Fiscal Geral", malhas ativas, relatórios. */
  | 'grafico'
  /** Sinal de porcentagem — efetividade do DTE. */
  | 'percentual'
  /** Medalha com fita — ação fiscal · Concluídas. */
  | 'trofeu'
  /** Calculadora — livro fiscal, emissão de guia. */
  | 'calculadora'
  // --- comunicação (§3.1: L07, L08, L09) ---
  /** Envelope aberto — nav "Domicílio Tributário". */
  | 'email'
  /** Balão de fala — Plantão Fiscal, atendimento. */
  | 'conversa'
  /** Bandeja de entrada — caixa de mensagens do DTE. */
  | 'caixa-entrada'
  /** Duas setas opostas — Serviços Tomados / Prestados. */
  | 'entrada-saida'
  // --- pessoas (§3.1: L25) ---
  /** Duas pessoas — sub-usuários, grupos, relatório de contribuintes. */
  | 'grupo'
  // --- ações (§3.1: L10, L18, L20, L23) ---
  /** Cadeado fechado — "Alterar Senha". */
  | 'cadeado'
  /** Disquete — "Salvar Permissões". */
  | 'salvar'
  /** Play em círculo — "Executar agora" do scheduler. */
  | 'executar'
  /** Seta em arco anti-horária — "Reverter exclusão". NÃO é o `atualizar`. */
  | 'reverter'
  /** X compacto — "Cancelar Parcelamento em Lote". */
  | 'cancelar'
  /** Círculo com check — "Aprovar todos", situação concluída. */
  | 'check-circulo'
  /** Círculo com X — "Reprovar todos", situação cancelada. */
  | 'fechar-circulo'
  /** Círculo com "!" — erro de credenciais, aviso em linha. NÃO é o triângulo. */
  | 'alerta-circulo';

/**
 * Tamanhos que o canvas instancia inline (§3.4), medidos nos 143 artboards:
 * 12 no chip de linha · 13 na barra de ferramentas do grid · 14 no chevron do
 * `.pgbtn` · 15 no `.iconbtn` e no `.searchbox` · 16 no `.btnp` · 17 no cabeçalho
 * de grupo · 18 padrão · 20 no item de nav · 22 no tile 40×40 do KPI · 24 no anexo
 * · 26 no estado vazio · 30 no cartão de serviço · 32 no slot de documento ·
 * 44 no ícone lateral do KPI.
 */
export type DsIconSize = 12 | 13 | 14 | 15 | 16 | 17 | 18 | 20 | 22 | 24 | 26 | 30 | 32 | 44;

export type DsIconProps = {
  name: DsIconName;
  /** Padrão: 18. */
  size?: DsIconSize;
  sx?: SxProps<Theme>;
};

// ----------------------------------------------------------------------

/** Nome do canvas -> um único iconset de traço, embutido. Fechado de propósito. */
const DS_TRACO: Record<DsIconName, IconifyName> = {
  // utilitários
  buscar: 'ds:buscar',
  mais: 'ds:mais',
  atualizar: 'ds:atualizar',
  editar: 'ds:editar',
  excluir: 'ds:excluir',
  olho: 'ds:olho',
  fechar: 'ds:fechar',
  'chevron-esq': 'ds:chevron-esq',
  'chevron-dir': 'ds:chevron-dir',
  'chevron-baixo': 'ds:chevron-baixo',
  'chevron-cima': 'ds:chevron-cima',
  filtro: 'ds:filtro',
  baixar: 'ds:baixar',
  enviar: 'ds:enviar',
  anexo: 'ds:anexo',
  calendario: 'ds:calendario',
  usuario: 'ds:usuario',
  alerta: 'ds:alerta',
  info: 'ds:info',
  check: 'ds:check',
  'mais-opcoes': 'ds:mais-opcoes',
  exportar: 'ds:exportar',
  imprimir: 'ds:imprimir',
  // documento e papel
  documento: 'ds:documento',
  guia: 'ds:guia',
  'documento-check': 'ds:documento-check',
  nota: 'ds:nota-fiscal',
  folha: 'ds:folha',
  prancheta: 'ds:prancheta',
  processo: 'ds:processo',
  extrato: 'ds:extrato',
  // dinheiro
  cifrao: 'ds:cifrao',
  moeda: 'ds:moeda',
  credito: 'ds:credito',
  pagamento: 'ds:pagamento',
  parcela: 'ds:parcela',
  parcelamento: 'ds:parcelamento',
  emissao: 'ds:emissao-guia',
  // tempo e medida
  relogio: 'ds:relogio',
  grafico: 'ds:grafico-barras',
  percentual: 'ds:percentual',
  trofeu: 'ds:trofeu',
  calculadora: 'ds:calculadora',
  // comunicação
  email: 'ds:email',
  conversa: 'ds:conversa',
  'caixa-entrada': 'ds:caixa-entrada',
  'entrada-saida': 'ds:entrada-saida',
  // pessoas
  grupo: 'ds:grupo',
  // ações
  cadeado: 'ds:cadeado',
  salvar: 'ds:salvar',
  executar: 'ds:executar',
  reverter: 'ds:reverter',
  cancelar: 'ds:cancelar',
  'check-circulo': 'ds:check-circulo',
  'fechar-circulo': 'ds:fechar-circulo',
  'alerta-circulo': 'ds:alerta-circulo',
};

/**
 * Espessura por tamanho, medida nos artboards. O canvas usa 1.7 no seletor `svg`
 * e sobrescreve inline: 1.4 no ícone lateral do KPI (44px), 1.6 no item de nav
 * (20px) e 1.6 nos glifos grandes de cartão e estado vazio (24/26/30/32px).
 */
function strokePorTamanho(size: DsIconSize): number {
  if (size === 44) return 1.4;
  if (size === 20) return 1.6;
  if (size >= 24) return 1.6;
  return 1.7;
}

// ----------------------------------------------------------------------

export function DsIcon({ name, size = 18, sx }: DsIconProps) {
  const strokeWidth = strokePorTamanho(size);

  // O traço precisa vencer o `stroke-width` que vem como atributo de apresentação
  // dentro do path do ícone — por isso o seletor desce até os filhos, e não só até
  // a raiz (a raiz do <Iconify> já É o <svg>).
  const traco = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const;

  return (
    <Iconify
      icon={DS_TRACO[name]}
      width={size}
      sx={[{ ...traco, '& svg, & *': traco }, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
}
